import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useEligibleCoworkers } from '@/shared/hooks/useEmployees';
import { useCreateOneOnOneWithReplace } from '../shared/hooks/useCreateOneOnOneWithReplace';
import { ReplaceOpenMeetingDialog } from './ReplaceOpenMeetingDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { UserSelectorChoiceList } from '@/shared/components/user-selector/UserSelectorChoiceList';
import type { UserSelectorEmployee } from '@/shared/components/user-selector/UserSelectorItem';

interface CreateCoworkerOneOnOneModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateCoworkerOneOnOneModal = ({
  open,
  onOpenChange,
}: CreateCoworkerOneOnOneModalProps) => {
  const { t } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');

  const { data: eligibleEmployeesData = [], isLoading: isLoadingEmployees } =
    useEligibleCoworkers(open);

  const eligibleEmployees = useMemo(() => {
    return eligibleEmployeesData.map((emp) => ({
      id: emp._id,
      firstName: emp.personalInfo.firstName,
      lastName: emp.personalInfo.lastName,
      fullName: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
      isActive: emp.isActive,
      imageUrl: emp.personalInfo.imageUrl,
      role: emp.role,
    })) as UserSelectorEmployee[];
  }, [eligibleEmployeesData]);

  const {
    createMeeting,
    confirmReplace,
    cancelReplace,
    isCreating,
    isReplacing,
    replaceDialogState,
  } = useCreateOneOnOneWithReplace();

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedEmployeeId(selectedIds[0] || '');
  };

  const handleCreate = async () => {
    if (!selectedEmployeeId) return;

    const selectedEmployee = eligibleEmployees.find(
      (emp) => emp.id === selectedEmployeeId
    );

    await createMeeting({
      employeeId: currentEmployee._id,
      managerId: selectedEmployeeId,
      companyId: currentEmployee.companyId,
      employeeName: selectedEmployee?.fullName || selectedEmployeeId,
    });

    // Only close modal on success
    onOpenChange(false);
    setSelectedEmployeeId('');
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedEmployeeId('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t('selectCoworker')}</DialogTitle>
          <DialogDescription>
            {t('selectCoworkerDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoadingEmployees ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : eligibleEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-base font-semibold text-foreground mb-2">
                {t('noCoworkersAvailable')}
              </p>
              <p className="text-sm text-muted-foreground max-w-xs">
                {t('noCoworkersAvailableDescription')}
              </p>
            </div>
          ) : (
            <UserSelectorChoiceList
              employees={eligibleEmployees}
              selectedEmployees={selectedEmployeeId ? [selectedEmployeeId] : []}
              onSelectionChange={handleSelectionChange}
              multiSelect={false}
              searchable={true}
              maxHeight="max-h-[400px]"
              columns={1}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isCreating}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!selectedEmployeeId || isCreating}
          >
            {isCreating ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent me-2" />
                {t('creating1on1WithCoworker')}
              </>
            ) : (
              t('create')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {replaceDialogState && (
        <ReplaceOpenMeetingDialog
          open={!!replaceDialogState}
          onOpenChange={cancelReplace}
          employeeName={replaceDialogState.employeeName}
          onConfirmReplace={confirmReplace}
          isReplacing={isReplacing}
        />
      )}
    </Dialog>
  );
};
